import { useState, useEffect, useRef } from 'react';
import { z } from 'zod';
import { useFormSubmit } from '../../../hooks/useFormSubmit';
import { useToast } from '../../../components/base/Toast';
import { PHONE_DISPLAY } from '../../../lib/businessInfo';

const ContactSchema = z.object({
  name: z.string().min(2, 'Please enter your full name (at least 2 characters)'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  subject: z.string().min(1, 'Please select a subject'),
  message: z.string().min(10, 'Message must be at least 10 characters').max(500, 'Message cannot exceed 500 characters'),
});

const EMPTY_FORM = { name: '', email: '', phone: '', subject: '', message: '' };

export default function ContactForm() {
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showSuccess, setShowSuccess] = useState(false);

  const { handleSubmit, isSubmitting, isSuccess, isError, validationErrors } = useFormSubmit({
    url: 'https://readdy.ai/api/form/d6j89lfrgrhbthj8mqd0',
    schema: ContactSchema,
    formRef,
    errorRef,
    conversionLabel: 'CONTACT_LEAD_LABEL',
    leadEventName: 'Contact Form',
    onError: () => toast.error('Submission failed', `Unable to send your message. Please try again or call us at ${PHONE_DISPLAY}.`),
  });

  useEffect(() => {
    if (isSuccess) setShowSuccess(true);
  }, [isSuccess]);

  // Move focus to success heading when success state renders
  useEffect(() => {
    if (showSuccess) {
      requestAnimationFrame(() => successHeadingRef.current?.focus());
    }
  }, [showSuccess]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReset = () => {
    setFormData(EMPTY_FORM);
    setShowSuccess(false);
  };

  if (showSuccess) {
    return (
      <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6 shadow-sm flex flex-col items-center text-center">
        <div className="w-12 h-12 flex items-center justify-center bg-[#F7F5F0] rounded-full mb-3">
          <i className="ri-mail-check-line text-2xl text-[#E8B84B]" aria-hidden="true"></i>
        </div>
        <h2
          ref={successHeadingRef}
          tabIndex={-1}
          className="font-['Oswald'] text-2xl font-semibold text-[#1C2B1E] mb-2 focus:outline-none"
        >
          Message Received!
        </h2>
        <p className="text-gray-600 text-sm mb-4 max-w-xs">
          We'll get back to you within 2 hours during business hours. No bots — just us.
        </p>
        <button
          onClick={handleReset}
          className="text-sm text-[#4A5568] underline hover:text-[#1C2B1E] transition-colors cursor-pointer"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D6] p-6 shadow-sm">
      <h2 className="font-['Oswald'] text-2xl font-semibold text-[#1C2B1E] mb-1">
        Drop Us a Line
      </h2>
      <p className="text-gray-600 text-sm mb-1">
        You're not submitting a support ticket. You're talking to two actual humans who will read every word you write.
      </p>
      <p className="text-[#92650a] font-semibold text-sm mb-4">
        No bots. No scripts. No "have you tried turning it off and on again?"
      </p>

      {/* Server / network error live region */}
      {isError && (
        <div
          ref={errorRef}
          tabIndex={-1}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 focus:outline-none"
        >
          <i className="ri-error-warning-line text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true"></i>
          <p className="text-red-700 text-sm">
            Unable to send your message. Please try again or call us at {PHONE_DISPLAY}.
          </p>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4" id="contact-form" data-readdy-form ref={formRef}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-1.5">
              <i className="ri-user-line text-[#E8B84B]" aria-hidden="true"></i>
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              aria-required="true"
              aria-invalid={!!validationErrors.name}
              aria-describedby={validationErrors.name ? 'name-error' : undefined}
              autoComplete="name"
              className={`w-full px-3 py-2.5 border rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus:border-transparent transition-all text-sm ${validationErrors.name ? 'border-red-400 bg-red-50' : 'border-[#E8E2D6]'}`}
              placeholder="John Doe"
            />
            {validationErrors.name && (
              <p id="name-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.name}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-1.5">
              <i className="ri-mail-line text-[#E8B84B]" aria-hidden="true"></i>
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              aria-required="true"
              aria-invalid={!!validationErrors.email}
              aria-describedby={validationErrors.email ? 'email-error' : undefined}
              autoComplete="email"
              className={`w-full px-3 py-2.5 border rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus:border-transparent transition-all text-sm ${validationErrors.email ? 'border-red-400 bg-red-50' : 'border-[#E8E2D6]'}`}
              placeholder="john@example.com"
            />
            {validationErrors.email && (
              <p id="email-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-1.5">
              <i className="ri-phone-line text-[#E8B84B]" aria-hidden="true"></i>
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              autoComplete="tel"
              className="w-full px-3 py-2.5 border border-[#E8E2D6] rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus:border-transparent transition-all text-sm"
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <label htmlFor="subject" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-1.5">
              <i className="ri-chat-3-line text-[#E8B84B]" aria-hidden="true"></i>
              Subject
            </label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              aria-required="true"
              aria-invalid={!!validationErrors.subject}
              aria-describedby={validationErrors.subject ? 'subject-error' : undefined}
              autoComplete="off"
              className={`w-full px-3 py-2.5 border rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus:border-transparent transition-all bg-white text-sm ${validationErrors.subject ? 'border-red-400 bg-red-50' : 'border-[#E8E2D6]'}`}
            >
              <option value="">Select a subject</option>
              <option value="general">General Inquiry</option>
              <option value="inventory">Vehicle Inventory</option>
              <option value="financing">Financing Question</option>
              <option value="trade-in">Trade-In Valuation</option>
              <option value="service">Service &amp; Maintenance</option>
              <option value="other">Other</option>
            </select>
            {validationErrors.subject && (
              <p id="subject-error" role="alert" className="text-red-500 text-xs mt-1">{validationErrors.subject}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="flex items-center gap-2 text-sm font-medium text-[#1C2B1E] mb-1.5">
            <i className="ri-message-3-line text-[#E8B84B]" aria-hidden="true"></i>
            Message
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            aria-required="true"
            aria-invalid={!!validationErrors.message}
            aria-describedby="message-char-count"
            rows={3}
            maxLength={500}
            className={`w-full px-3 py-2.5 border rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus:border-transparent transition-all resize-none text-sm ${validationErrors.message ? 'border-red-400 bg-red-50' : 'border-[#E8E2D6]'}`}
            placeholder="Tell us what's on your mind..."
          />
          <div className="flex justify-between items-center mt-1">
            {validationErrors.message ? (
              <p role="alert" className="text-red-500 text-xs">{validationErrors.message}</p>
            ) : <span />}
            <p id="message-char-count" className="text-xs text-[#4A5568]" aria-live="polite">{formData.message.length}/500</p>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#1A2332] text-white py-3 rounded-lg font-semibold text-base hover:bg-[#2C3E55] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
              Sending...
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill" aria-hidden="true"></i>
              Send Message
            </>
          )}
        </button>
      </form>
    </div>
  );
}